import axios, {
  AxiosResponse,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { getEventBus } from "@jt-home/jt-event-bus";
import {
  apiConfig as defaultApiConfig,
  layerWhitelist,
  requestRefresh,
  requestUserInfo,
} from "./api.config";
import { localStorageAuth } from "./storageAuth";
import { matchPath } from "./matchPath";
import { createHandleLoginExpired } from "./handleLoginExpired";
import type {
  CreateAxiosConfig,
  CreateAxiosInstance,
  CustomAxiosRequestConfig,
  PathPattern,
  RequestHttpEventMap,
} from "./types";

export const requestHttpBus = getEventBus<RequestHttpEventMap>();

function mergeWhitelist(
  layer: { noTokenPaths: PathPattern[]; skipRefreshPaths: PathPattern[] },
  project: CreateAxiosConfig,
): { noTokenPaths: PathPattern[]; skipRefreshPaths: PathPattern[] } {
  return {
    noTokenPaths: [...layer.noTokenPaths, ...(project.noTokenPaths ?? [])],
    skipRefreshPaths: [
      ...layer.skipRefreshPaths,
      ...(project.skipRefreshPaths ?? []),
    ],
  };
}

type HeadersLike =
  | Record<string, string>
  | {
      set?: (key: string, value: string) => void;
      get?: (key: string) => string | null;
    };

export function attachTokenToConfig(
  config: { headers?: HeadersLike },
  token: string,
  options?: { contentType?: string; accept?: string },
): void {
  const headers = config.headers;
  if (!headers) return;

  const contentType = options?.contentType ?? "application/json; charset=UTF-8";
  const accept = options?.accept ?? "application/json; charset=UTF-8";

  if (
    typeof (headers as { set?: (key: string, value: string) => void }).set ===
    "function"
  ) {
    const normalizedHeaders = headers as {
      set: (key: string, value: string) => void;
      get: (key: string) => string | null;
    };
    normalizedHeaders.set("x-access-token", token);
    if (!normalizedHeaders.get?.("Content-Type")) {
      normalizedHeaders.set("Content-Type", contentType);
    }
    normalizedHeaders.set("Accept", accept);
  } else {
    const normalizedHeaders = headers as Record<string, string>;
    normalizedHeaders["x-access-token"] = token;
    if (!normalizedHeaders["Content-Type"]) {
      normalizedHeaders["Content-Type"] = contentType;
    }
    normalizedHeaders.Accept = accept;
  }
}

export function createAxios(
  config: CreateAxiosConfig,
): AxiosInstance & CreateAxiosInstance {
  const {
    apiConfig,
    bus = requestHttpBus,
    getToken = localStorageAuth.getToken,
    setToken = localStorageAuth.setToken,
    getRefreshToken = localStorageAuth.getRefreshToken,
    setRefreshToken = localStorageAuth.setRefreshToken,
    clearAuth = localStorageAuth.clearAuth,
    onUnauthorized,
    loginExpiredCodes = ["401"],
    ...axiosConfig
  } = config;

  const whitelist = mergeWhitelist(layerWhitelist, config);
  const mergedApiConfig = {
    ...defaultApiConfig,
    ...apiConfig,
    refreshToken: {
      ...defaultApiConfig.refreshToken,
      ...apiConfig?.refreshToken,
    },
    getUserInfo: {
      ...defaultApiConfig.getUserInfo,
      ...apiConfig?.getUserInfo,
    },
  };

  function fireUnauthorized(message?: string) {
    bus.emit("unauthorized", message);
    onUnauthorized?.(message);
  }

  const instance = axios.create({
    baseURL: axiosConfig.baseURL,
    timeout: axiosConfig.timeout ?? 15000,
    headers: axiosConfig.headers,
    ...axiosConfig,
  });

  const innerInstance = axios.create({
    baseURL: axiosConfig.baseURL,
    timeout: axiosConfig.timeout ?? 15000,
    headers: axiosConfig.headers,
    ...axiosConfig,
  });

  let refreshPromise: Promise<void> | null = null;

  function shouldSkipToken(url: string): boolean {
    return matchPath(url, whitelist.noTokenPaths);
  }

  function shouldSkipRefresh(url: string): boolean {
    return matchPath(url, whitelist.skipRefreshPaths);
  }

  function doRefresh(): Promise<void> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearAuth();
      fireUnauthorized();
      return Promise.reject(new Error("No refresh token"));
    }

    return requestRefresh(instance, refreshToken, mergedApiConfig)
      .then(async (res) => {
        if (!res.accessToken) {
          clearAuth();
          fireUnauthorized();
          throw new Error("Refresh failed");
        }

        setToken(res.accessToken);
        if (res.refreshToken && setRefreshToken) {
          setRefreshToken(res.refreshToken);
        }

        bus.emit("changed:token", res.accessToken);

        try {
          requestUserInfo(instance, mergedApiConfig).then((userInfo) => {
            bus.emit("changed:userInfo", userInfo);
          });
        } catch (error) {
          console.warn("[createAxios] getUserInfo after refresh failed:", error);
        }
      })
      .catch((error) => {
        clearAuth();
        fireUnauthorized();
        throw error;
      });
  }

  const handleLoginExpired = createHandleLoginExpired({
    instance: innerInstance,
    getRefreshToken,
    clearAuth,
    fireUnauthorized,
    shouldSkipRefresh,
    doRefresh,
    getRefreshPromise: () => refreshPromise,
    setRefreshPromise: (promise) => {
      refreshPromise = promise;
    },
  });

  instance.interceptors.request.use(
    async (req: InternalAxiosRequestConfig & CustomAxiosRequestConfig) => {
      const url = req.url ?? "";
      const skipToken = shouldSkipToken(url);
      const skipRefresh =
        req.skipTokenRefresh === true || shouldSkipRefresh(url);

      if (refreshPromise && !skipToken && !skipRefresh) {
        await refreshPromise;
      }

      if (!skipToken) {
        const token = getToken();
        if (token) {
          attachTokenToConfig(req, token);
        }
      }

      if (!req.headers.get("Content-Type")) {
        req.headers.set("Content-Type", "application/json; charset=UTF-8");
      }

      return req;
    },
    (error) => Promise.reject(error),
  );

  innerInstance.interceptors.request.use(
    (req: InternalAxiosRequestConfig & CustomAxiosRequestConfig) => {
      const url = req.url ?? "";
      if (!shouldSkipToken(url)) {
        const token = getToken();
        if (token) {
          attachTokenToConfig(req, token);
        }
        if (!req.headers.get("Content-Type")) {
          req.headers.set("Content-Type", "application/json; charset=UTF-8");
        }
      }

      return req;
    },
    (error) => Promise.reject(error),
  );

  function isLoginExpiredCode(value: unknown): boolean {
    if (value === undefined || value === null) return false;
    return loginExpiredCodes.some((code) => String(code) === String(value));
  }

  async function processResponse(response: unknown): Promise<unknown> {
    const code = (response as { data?: { code?: unknown } })?.data?.code;
    if (!isLoginExpiredCode(code)) return Promise.resolve(response);
    return handleLoginExpired(response as {
      config: InternalAxiosRequestConfig & CustomAxiosRequestConfig;
      data?: unknown;
    });
  }

  instance.interceptors.response.use(
    (response) =>
      processResponse(response) as Promise<import("axios").AxiosResponse>,
    (error) => Promise.reject(error),
  );

  return Object.assign(instance, {
    on: bus.on.bind(bus),
    off: bus.off.bind(bus),
    bus,
  });
}

export { apiConfig, layerWhitelist } from "./api.config";
export { createLocalStorageAuth, localStorageAuth } from "./storageAuth";
export { matchPath } from "./matchPath";
export type {
  CreateAxiosConfig,
  CreateAxiosInstance,
  ApiConfig,
  WhitelistConfig,
  PathPattern,
  CustomAxiosRequestConfig,
  RefreshTokenResult,
  RequestHttpEventMap,
} from "./types";
export type { LocalStorageAuth, LocalStorageAuthOptions } from "./storageAuth";
export type {
  LoginExpiredContext,
  LoginExpiredPayload,
} from "./handleLoginExpired";
export { createHandleLoginExpired } from "./handleLoginExpired";
